import { useState, useEffect, useCallback } from 'react';
import { getWaiters, verifyPin } from '../api/pin.api.js';
import { useWaiterStore } from '../store/waiterStore.js';
import { IcoUser, IcoClose } from './common/Icons.jsx';
import styles from './WaiterPinSheet.module.css';
import { useAtrasCierra } from '../lib/atras.js';

// Teclado de PIN para la tablet compartida del salón. Se teclea el PIN, no se
// elige un nombre de la lista: si bastara con tocar el nombre, cualquiera se
// atribuiría las propinas de otro.
//
// La lista de nombres se muestra solo como referencia de quién puede entrar.
export default function WaiterPinSheet({ open, onClose, onDone, para = null }) {
  useAtrasCierra(open, onClose);
  const [pin,      setPin]      = useState('');
  const [equipo,   setEquipo]   = useState([]);
  const [error,    setError]    = useState(null);
  const [enviando, setEnviando] = useState(false);
  // Tras cinco PIN incorrectos el servidor bloquea ESTE aparato unos minutos
  // (los demás siguen). Mientras dura, el teclado se apaga y se ve cuánto
  // falta: teclear contra un bloqueo solo lo hacía parecer descompuesto.
  const [bloqueoHasta, setBloqueoHasta] = useState(0);
  const [ahora, setAhora] = useState(() => Date.now());
  const setWaiter = useWaiterStore((s) => s.setWaiter);

  useEffect(() => {
    if (!open) return;
    setPin(''); setError(null);
    getWaiters().then(setEquipo).catch(() => setEquipo([]));
  }, [open]);

  const bloqueado = bloqueoHasta > ahora;
  useEffect(() => {
    if (!bloqueado) return undefined;
    const t = setInterval(() => setAhora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [bloqueado]);
  // Al vencer el bloqueo se va también su mensaje
  useEffect(() => {
    if (bloqueoHasta && !bloqueado) { setBloqueoHasta(0); setError(null); }
  }, [bloqueado]); // eslint-disable-line react-hooks/exhaustive-deps
  const restante = Math.max(0, Math.ceil((bloqueoHasta - ahora) / 1000));
  const reloj = `${Math.floor(restante / 60)}:${String(restante % 60).padStart(2, '0')}`;

  const enviar = useCallback(async (valor) => {
    setEnviando(true); setError(null);
    try {
      const w = await verifyPin(valor);
      setWaiter(w);
      onDone?.(w);
      onClose?.();
    } catch (err) {
      const datos = err.response?.data;
      if (err.response?.status === 429 && datos?.reintentarEn) {
        setBloqueoHasta(Date.now() + datos.reintentarEn * 1000);
        setAhora(Date.now());
      }
      setError(datos?.error ?? 'No se pudo verificar el PIN');
      setPin('');
    } finally { setEnviando(false); }
  }, [setWaiter, onDone, onClose]);

  // La actualización va en forma funcional a propósito: en una tablet los
  // toques llegan más rápido que los re-renders, y leer `pin` del closure hacía
  // que cuatro toques seguidos dejaran un solo dígito.
  const teclear = (d) => {
    if (enviando || bloqueado) return;
    setError(null);
    setPin((prev) => (prev + d).slice(0, 6));
  };

  // Con 4 dígitos se envía solo, pero después de un instante sin teclear: si
  // el PIN es de 5 o 6 el mesero sigue tecleando y no se manda a medias. Antes
  // se enviaba al cuarto dígito y un PIN de 5 o 6 no se podía capturar nunca
  // (cada intento fallido además contaba contra todo el equipo).
  useEffect(() => {
    if (pin.length !== 4 || enviando) return undefined;
    const t = setTimeout(() => enviar(pin), 800);
    return () => clearTimeout(t);
  }, [pin]);   // enviar/enviando fuera a propósito: solo dispara al cambiar el PIN

  if (!open) return null;

  return (
    <div className={styles.scrim} role="presentation" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={styles.sheet} role="dialog" aria-modal="true" aria-label="Identifícate con tu PIN">
        <div className={styles.head}>
          <h2 className={styles.title}>¿Quién eres?</h2>
          <button className={styles.close} onClick={onClose} aria-label="Cerrar"><IcoClose size={16} /></button>
        </div>
        {/* Qué mesa estás tomando. La hoja preguntaba quién eres sin decir para
            qué, y el mesero ya no sabía si tocó la mesa correcta. */}
        {para && <p className={styles.para}>Vas a tomar <strong>{para}</strong></p>}
        <p className={styles.hint}>Teclea tu PIN para que las comandas y las propinas queden a tu nombre.</p>

        <div className={styles.dots} aria-label={`${pin.length} dígitos`}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span key={i} className={`${styles.dot} ${i < pin.length ? styles.dotOn : ''} ${i >= 4 ? styles.dotOpt : ''}`} />
          ))}
        </div>

        {error && <p className={styles.error}>{error}</p>}
        {bloqueado && <p className={styles.reloj} aria-live="polite">Podrás intentar de nuevo en {reloj}</p>}

        <div className={styles.pad}>
          {[1,2,3,4,5,6,7,8,9].map((d) => (
            <button key={d} className={styles.key} onClick={() => teclear(String(d))} disabled={enviando || bloqueado}>{d}</button>
          ))}
          <button className={styles.keyGhost} onClick={() => { setPin(''); if (!bloqueado) setError(null); }} disabled={enviando}>Borrar</button>
          <button className={styles.key} onClick={() => teclear('0')} disabled={enviando || bloqueado}>0</button>
          <button
            className={styles.keyOk}
            onClick={() => pin.length >= 4 && enviar(pin)}
            disabled={enviando || bloqueado || pin.length < 4}
          >{enviando ? '…' : 'Entrar'}</button>
        </div>

        {equipo.length > 0 && (
          <p className={styles.equipo}>
            <IcoUser size={12} /> {equipo.map((u) => u.name).join(' · ')}
          </p>
        )}
      </div>
    </div>
  );
}
